// [NEW FILE] Reports: "Where we're heading". Answers two questions at a glance:
// - Is performance going up or down? (average released score, latest cycle vs the one before, with the cycle line)
// - Is progress on pace? (share of appraisals past the stage open today vs the share of its time used; overdue count)
import { FiTrendingUp, FiTrendingDown, FiMinus, FiAlertTriangle, FiCheckCircle, FiClock } from "react-icons/fi";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import useAsync from "../../../hooks/useAsync";
import { getHeading } from "../../../services/reportApi";
import { Card } from "../../../components/common";
import { axisProps, tooltipProps, CHART_SERIES } from "../../../utils/chartTheme";
import { formatRatingScore } from "../../../utils/ratingScale";

const VERDICT = {
  "on-track": { label: "On track", tone: "success", icon: FiCheckCircle },
  "at-risk": { label: "At risk", tone: "warning", icon: FiClock },
  behind: { label: "Behind", tone: "danger", icon: FiAlertTriangle },
};

function Bar2({ label, value, tone }) {
  return (
    <div className="heading-bar">
      <span className="heading-bar-label">{label}</span>
      <span className="heading-bar-track"><span className={`heading-bar-fill tone-${tone}`} style={{ width: `${value}%` }} /></span>
      <span className="heading-bar-value">{value}%</span>
    </div>
  );
}

export default function ReportsHeading({ cycle }) {
  const { data } = useAsync(() => getHeading(cycle), [cycle?.id]);
  if (!data) return null;
  const { performance: perf, progress } = data;

  let perfView = <p className="muted">No released results yet. The direction shows once results are released in two cycles.</p>;
  if (perf?.latest && perf.change === null) {
    perfView = <p className="muted">Average score {formatRatingScore(perf.latest.score)} in {perf.latest.name}. The direction shows once a second cycle has released results.</p>;
  } else if (perf?.latest) {
    const up = perf.change > 0.5;
    const down = perf.change < -0.5;
    const Icon = up ? FiTrendingUp : down ? FiTrendingDown : FiMinus;
    const tone = up ? "success" : down ? "danger" : "neutral";
    perfView = (
      <>
        <p className={`heading-verdict tone-${tone}`}>
          <Icon size={20} aria-hidden="true" />
          <strong>{up ? "Improving" : down ? "Declining" : "Steady"}</strong>
          <span>{perf.change > 0 ? "+" : ""}{formatRatingScore(perf.change)} points vs {perf.previous.name}</span>
        </p>
        <p className="muted">Average score {formatRatingScore(perf.latest.score)} in {perf.latest.name}, {formatRatingScore(perf.previous.score)} in {perf.previous.name}.</p>
        {perf.series.length >= 2 && (
          <div style={{ height: 140 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={perf.series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" {...axisProps} />
                <YAxis width={36} {...axisProps} />
                <Tooltip {...tooltipProps} formatter={(v) => [formatRatingScore(v), "Average score"]} />
                <Line type="monotone" dataKey="value" stroke={CHART_SERIES[0]} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </>
    );
  }

  let progressView = <p className="muted">Progress is shown for the selected cycle when you can see its appraisals.</p>;
  if (progress) {
    const pace = progress.pace;
    const v = pace ? VERDICT[pace.verdict] : progress.overdue ? VERDICT.behind : VERDICT["on-track"];
    const Icon = v.icon;
    progressView = (
      <>
        <p className={`heading-verdict tone-${v.tone}`}>
          <Icon size={20} aria-hidden="true" />
          <strong>{v.label}</strong>
          <span>{pace ? `${pace.stage}: ${pace.done} of ${pace.total} done, ${pace.daysLeft} day${pace.daysLeft === 1 ? "" : "s"} left` : "No stage window is open today"}</span>
        </p>
        {pace && (
          <>
            <Bar2 label="Appraisals done" value={pace.donePct} tone={v.tone} />
            <Bar2 label="Time used" value={pace.timePct} tone="neutral" />
          </>
        )}
        <p className={progress.overdue ? "heading-overdue" : "muted"}>
          {progress.overdue
            ? `${progress.overdue} of ${progress.total} appraisals are overdue (still before the end of ${progress.mustReachLabel}). Send reminders from Reminders.`
            : "No appraisals are overdue."}
        </p>
      </>
    );
  }

  return (
    <Card title="Where we're heading" subtitle="Direction of performance across cycles, and whether this cycle is on pace.">
      <div className="heading-grid">
        <section aria-label="Performance direction"><h3 className="heading-title">Performance</h3>{perfView}</section>
        <section aria-label="Progress pace"><h3 className="heading-title">Progress in {cycle?.name || "this cycle"}</h3>{progressView}</section>
      </div>
    </Card>
  );
}
